<template>
  <core-modal-marketing
    :tag="content.modalEyebrow"
    tag-icon="i-ri-magic-line"
    :title="content.modalTitle"
    :subtitle="content.modalDescription"
    header-icon="i-ri-image-edit-line"
  >
    <div class="mx-auto max-w-6xl">
      <ClientOnly>
        <template v-if="pending">
          <div class="grid gap-4 lg:grid-cols-3">
            <core-upsell-product-card-skeleton
              v-for="index in 3"
              :key="index"
            />
          </div>
        </template>

        <core-contextual-error-box
          v-else-if="fetchError"
          size="sm"
          class="my-3 rounded-md border-2 border-gray-200"
          :action="retry"
        >
          <template #heading>Unable to Load Restoration Options</template>
          <template #default>Please try again or contact support.</template>
        </core-contextual-error-box>

        <template v-else>
          <section>
            <p class="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
              Choose a restoration option
            </p>

            <div class="mt-2 grid gap-4 lg:grid-cols-3">
              <article
                v-for="product in displayProducts"
                :key="product.id"
                class="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div class="flex flex-1 items-start gap-4">
                  <div
                    class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border"
                    :class="product.iconContainerClass"
                  >
                    <u-icon
                      :name="product.icon"
                      class="h-6 w-6"
                      :class="product.iconClass"
                    />
                  </div>
                  <div class="min-w-0">
                    <h2 class="text-lg font-semibold text-slate-900">
                      {{ product.name }}
                    </h2>
                    <p class="mt-1.5 text-sm leading-6 text-slate-600">
                      {{ product.description }}
                    </p>
                  </div>
                </div>

                <div class="mt-6">
                  <core-price-display
                    class="text-3xl"
                    :base-price="product.price_cents"
                    :discounted-price="product.promotionPrice"
                    :precision="2"
                    :precision-slashed="2"
                    :show-suffix="false"
                  />
                  <span class="text-base font-medium text-slate-500">
                    USD/per photo
                  </span>
                </div>

                <div class="mt-auto pt-6">
                  <u-button
                    color="primary"
                    size="lg"
                    block
                    class="justify-center"
                    label="Add to Cart"
                    :loading="addingProductId === product.id"
                    :disabled="Boolean(addingProductId)"
                    @click="addToCart(product)"
                  />
                </div>
              </article>
            </div>
          </section>

          <core-upsell-features
            class="mt-8 text-left"
            :items="content.included"
          />

          <p class="mt-8 text-center">
            <u-link
              :to="content.ctaTo"
              external
              class="text-sm text-gray-500 underline"
            >
              Learn More About FOREVER Photo Restoration
            </u-link>
          </p>
        </template>
      </ClientOnly>
    </div>
  </core-modal-marketing>
</template>

<script setup>
  import {EXTERNAL_URLS} from '~/constants/external-urls.js';

  const props = defineProps({
    upsellTracking: Object
  });

  const RESTORATION_PRODUCT_GROUP = 'restoration-products';
  const PRODUCT_DEFINITIONS = [
    {
      slug: 'photo-restoration-digital-delivery',
      name: 'Photo Restoration',
      description: 'Repair tears, water damage, fading, creases, and other wear.',
      icon: 'i-ri-image-edit-line',
      iconClass: 'text-sky-600',
      iconContainerClass: 'border-sky-200 bg-sky-50'
    },
    {
      slug: 'color-restoration-digital-delivery',
      name: 'Photo Colorization',
      description: 'Add realistic color to black-and-white photos while preserving authenticity.',
      icon: 'i-ri-palette-line',
      iconClass: 'text-violet-600',
      iconContainerClass: 'border-violet-200 bg-violet-50'
    },
    {
      slug: 'photo-restoration-colorization-digital-delivery',
      name: 'Restoration & Colorization Package',
      description: 'Restore damage and add handcrafted color to a black-and-white original.',
      icon: 'i-ri-magic-line',
      iconClass: 'text-rose-600',
      iconContainerClass: 'border-rose-200 bg-rose-50'
    }
  ];

  const content = {
    modalEyebrow: 'Photo Restoration',
    modalTitle: 'Bring old memories back to life.',
    modalDescription: 'FOREVER Photo Restoration and Colorization helps repair damage, restore clarity, and preserve the people and stories that matter most.',
    ctaTo: EXTERNAL_URLS.photoRestoration,
    included: [
      {
        title: 'Restoring with Care',
        description: 'Skilled artists give every photo personalized attention while preserving the memories it holds.',
        icon: 'i-ri-heart-3-fill'
      },
      {
        title: 'Precision Restoration',
        description: 'Advanced tools repair damage and enhance details while protecting the character of the original.',
        icon: 'i-ri-tools-fill'
      },
      {
        title: 'Handcrafted Colorization',
        description: 'Artists apply accurate, lifelike color by hand instead of relying on generic automated results.',
        icon: 'i-ri-palette-fill'
      }
    ]
  };

  const {
    products,
    pending,
    error,
    retry
  } = usePricingGroup({
    groupSlug: RESTORATION_PRODUCT_GROUP,
    includeDescriptions: true,
    server: false
  });

  const mappedProducts = computed(() => PRODUCT_DEFINITIONS.map(definition => {
    const product = products.value?.find(item => item.slug === definition.slug);

    if (!product) {
      return null;
    }

    return {
      ...product,
      name: definition.name,
      description: definition.description,
      icon: definition.icon,
      iconClass: definition.iconClass,
      iconContainerClass: definition.iconContainerClass
    };
  }));

  const hasRequiredProducts = computed(() => mappedProducts.value.every(Boolean));
  const fetchError = computed(() =>
    !pending.value &&
    (
      Boolean(error.value) ||
      !hasRequiredProducts.value
    )
  );
  const displayProducts = computed(() =>
    hasRequiredProducts.value ? mappedProducts.value : []
  );

  const addingProductId = ref(null);

  async function addToCart(product) {
    if (addingProductId.value) {
      return;
    }

    addingProductId.value = product.id;

    try {
      trackUpsellCta({
        tracking: props.upsellTracking,
        product
      });

      await usePricingApi().addToCart({
        productId: product.id,
        promotionCode: product.promotionCode
      });

      window.location = EXTERNAL_URLS.cart;
    } catch {
      useErrorToast().add('Unable to add to cart at this time. Please try again later.');
    } finally {
      addingProductId.value = null;
    }
  }
</script>
